import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Library, ListChecks, Stamp, ShieldAlert, Siren, Server, ShieldCheck, GitPullRequest, Layers,
  Building2, Wrench, ScrollText, Users, FolderLock, Plug, Mic, FileCode2, LogOut, Menu, Languages, Shield,
} from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { ReadAloud } from "./ReadAloud";
import { VoiceConsole } from "./VoiceConsole";
import { useI18n } from "../lib/i18n";
import { useAuth, MANAGER_ROLES, USER_READ_ROLES } from "../context/AuthContext";

const NAV = [
  { section: "nav_work", items: [
    { to: "/dashboard", key: "nav_dashboard", icon: LayoutDashboard },
    { to: "/use-cases", key: "nav_usecases", icon: Library },
    { to: "/tasks", key: "nav_tasks", icon: ListChecks },
    { to: "/approvals", key: "nav_approvals", icon: Stamp },
  ] },
  { section: "nav_registers", items: [
    { to: "/registers/risks", key: "nav_risks", icon: ShieldAlert },
    { to: "/registers/incidents", key: "nav_incidents", icon: Siren },
    { to: "/registers/assets", key: "nav_assets", icon: Server },
    { to: "/registers/controls", key: "nav_controls", icon: ShieldCheck },
    { to: "/registers/changes", key: "nav_changes", icon: GitPullRequest },
    { to: "/registers/services", key: "nav_services", icon: Layers },
    { to: "/registers/vendors", key: "nav_vendors", icon: Building2 },
    { to: "/registers/remediations", key: "nav_remediations", icon: Wrench },
  ] },
  { section: "nav_admin", items: [
    { to: "/audit", key: "nav_audit", icon: ScrollText, roles: MANAGER_ROLES },
    { to: "/users", key: "nav_users", icon: Users, roles: USER_READ_ROLES },
    { to: "/files", key: "nav_files", icon: FolderLock, adminOnly: true },
    { to: "/integrations", key: "nav_integrations", icon: Plug },
  ] },
  { section: "nav_ai", items: [
    { to: "/voice", key: "nav_voice", icon: Mic },
    { to: "/build-prompt", key: "nav_prompt", icon: FileCode2 },
  ] },
];

const NavList = ({ onNavigate }) => {
  const { t } = useI18n();
  const { hasRole, isAdmin } = useAuth();
  return (
    <nav className="space-y-6">
      {NAV.map((s) => (
        <div key={s.section}>
          <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t(s.section)}</p>
          <div className="space-y-1">
            {s.items.filter((i) => (i.adminOnly ? isAdmin : !i.roles || hasRole(i.roles))).map((i) => (
              <NavLink key={i.to} to={i.to} onClick={onNavigate} data-testid={`nav-${i.to.replace(/\//g, "-").slice(1)}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                <i.icon className="h-4 w-4 shrink-0" />{t(i.key)}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
};

const Brand = () => {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-3 px-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-600"><Shield className="h-5 w-5 text-white" /></div>
      <div className="leading-tight">
        <p className="text-sm font-bold text-white">{t("appName")}</p>
        <p className="text-[11px] text-slate-400">{t("appTag")}</p>
      </div>
    </div>
  );
};

export default function Layout() {
  const { t, lang, setLang, dir } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const side = dir === "rtl" ? "right" : "left";

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-64 overflow-y-auto bg-slate-900 py-6 lg:block">
        <Brand /><div className="mt-8 px-3"><NavList /></div>
      </aside>
      <Sheet open={mobileNav} onOpenChange={setMobileNav}>
        <SheetContent side={side} className="w-72 overflow-y-auto border-0 bg-slate-900 p-4 text-white">
          <SheetHeader><SheetTitle className="sr-only">{t("menu")}</SheetTitle></SheetHeader>
          <Brand /><div className="mt-6"><NavList onNavigate={() => setMobileNav(false)} /></div>
        </SheetContent>
      </Sheet>
      <Sheet open={voiceOpen} onOpenChange={setVoiceOpen}>
        <SheetContent side={dir === "rtl" ? "left" : "right"} className="flex w-full flex-col sm:max-w-lg" data-testid="voice-assistant-drawer">
          <SheetHeader><SheetTitle>{t("voiceTitle")}</SheetTitle></SheetHeader>
          <div className="flex-1 pt-2"><VoiceConsole /></div>
        </SheetContent>
      </Sheet>
      <div className="lg:ps-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-slate-200 bg-white/95 px-4 backdrop-blur-md sm:px-6">
          <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileNav(true)} aria-label={t("menu")}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1 truncate text-sm text-slate-500">
            <span className="hidden sm:inline">{t("welcome")}, </span><span data-testid="header-user-name" className="font-semibold text-slate-900">{user?.name}</span>
            <span data-testid="header-user-role" className="ms-2 hidden rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 md:inline">{user?.role}</span>
          </div>
          <ReadAloud />
          <Button data-testid="open-voice-assistant" variant="outline" size="icon" className="rounded-xl border-slate-200" onClick={() => setVoiceOpen(true)} aria-label={t("voiceTitle")}>
            <Mic className="h-5 w-5" />
          </Button>
          <Button data-testid="language-toggle" variant="outline" className="rounded-xl border-slate-200 px-3" onClick={() => setLang(lang === "en" ? "ar" : "en")}>
            <Languages className="h-4 w-4" /><span className="ms-1 text-xs font-semibold">{lang === "en" ? "عربي" : "EN"}</span>
          </Button>
          <Button data-testid="logout-button" variant="ghost" size="icon" onClick={async () => { await logout(); nav("/login"); }} aria-label={t("logout")}>
            <LogOut className="h-5 w-5 rtl:rotate-180" />
          </Button>
        </header>
        <main className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
